import { api } from "@/lib/api-client";

// ── Tenant-side customer management ─────────────────────────────────────────
export type CustomerRef = { id: string; name: string };
export type NumberRef = { id: string; number: string };

/** A plan the tenant sells to their own customers. */
export type CustomerPlan = {
  id: string;
  name: string;
  description: string;
  included_minutes: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
  display_order: number;
  /** Derived: price ÷ minutes. Not editable — top-ups use the plan's own rate. */
  rate_per_minute_cents: number;
  customer_count: number;
  created_at: string;
};

export type CustomerPlanInput = {
  name: string;
  description?: string;
  included_minutes: number;
  price_cents: number;
  is_active?: boolean;
};

/** Minutes used and left this period.
 *
 * Comes from one place on the server, so the tenant's Customers page and the
 * customer's own plan page can never quote different numbers.
 */
export type CustomerUsage = {
  plan: {
    id: string;
    name: string;
    description: string;
    price_cents: number;
    currency: string;
    included_minutes: number;
    rate_per_minute_cents: number;
  } | null;
  period_start: string;
  period_end: string;
  included_minutes: number;
  extra_minutes: number;
  minutes_allowed: number;
  minutes_used: number;
  minutes_left: number;
  percent_used: number;
  /** False for a customer with no plan: nothing counted, nothing blocked. */
  metered: boolean;
  /** True once the allowance is spent — new calls are refused. */
  blocked: boolean;
};

export type MinuteGrant = {
  id: string;
  minutes: number;
  note: string;
  created_at: string;
  created_by?: string;
};

export type Customer = {
  id: string;
  name: string;
  email: string;
  status: "active" | "paused";
  notes: string;
  created_at: string;
  assistant_count: number;
  phone_number_count: number;
  assistants: CustomerRef[];
  phone_numbers: NumberRef[];
  usage: CustomerUsage;
  plan: CustomerPlan | null;
};

/** Everything one customer owns — assigned by the tenant or built themselves. */
export type CustomerResources = {
  agents: CustomerRef[];
  assistants: CustomerRef[];
  flows: CustomerRef[];
  knowledge_bases: CustomerRef[];
  phone_numbers: CustomerRef[];
  webhooks: CustomerRef[];
};

/** The customer's row, in full — only returned for a single customer. */
export type CustomerDetail = Customer & {
  resources: CustomerResources;
  login: { email: string; is_active: boolean; last_login: string | null } | null;
  billing: {
    /** What this customer costs the tenant per month. */
    fee_cents: number;
    /** And per minute they speak. */
    per_minute_cents: number;
    next_charge_at: string | null;
  };
};

export type CustomerCall = {
  id: string;
  direction: "inbound" | "outbound";
  status: string;
  from_number: string;
  to_number: string;
  started_at: string | null;
  duration_seconds: number;
  /** Minutes taken off their allowance — rounded up, as counted. */
  minutes: number;
  answered_by: string;
  on_number: string;
  sentiment: string;
};

export type Assignable = {
  assistants: { id: string; name: string; assigned: boolean }[];
  phone_numbers: { id: string; number: string; assigned: boolean }[];
};

/** What one more customer costs — read live from the plan, never assumed. */
export type BillingPreview = {
  fee_cents: number;
  per_minute_cents: number;
  balance_cents: number;
  balance_after_cents: number;
  affordable: boolean;
  active_customers: number;
  monthly_total_after_cents: number;
  next_charge_on: string;
  configured: boolean;
};

type Paginated<T> = { count: number; results: T[] };

export const customersApi = {
  list: async () => {
    const res = await api.get<Paginated<Customer> | Customer[]>("/customers/");
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (data: {
    name: string;
    email: string;
    password: string;
    notes?: string;
    plan_id: string;
  }) => {
    const res = await api.post<Customer>("/customers/", data);
    return res.data;
  },
  update: async (
    id: string,
    data: Partial<Pick<Customer, "name" | "status" | "notes">> & { plan_id?: string },
  ) => {
    const res = await api.patch<Customer>(`/customers/${id}/`, data);
    return res.data;
  },
  get: async (id: string) => {
    const res = await api.get<CustomerDetail>(`/customers/${id}/`);
    return res.data;
  },
  calls: async (id: string, limit = 25) => {
    const res = await api.get<CustomerCall[]>(`/customers/${id}/calls/`, {
      params: { limit },
    });
    return res.data;
  },
  remove: async (id: string) => {
    await api.delete(`/customers/${id}/`);
  },
  assign: async (id: string, data: { assistant_ids?: string[]; phone_number_ids?: string[] }) => {
    const res = await api.post<Customer>(`/customers/${id}/assign/`, data);
    return res.data;
  },
  resetPassword: async (id: string, password: string) => {
    await api.post(`/customers/${id}/reset_password/`, { password });
  },
  /** Minutes on top of the plan. Negative takes them back. */
  addMinutes: async (id: string, minutes: number, note = "") => {
    const res = await api.post<Customer>(`/customers/${id}/add-minutes/`, {
      minutes,
      note,
    });
    return res.data;
  },
  minuteGrants: async (id: string) => {
    const res = await api.get<MinuteGrant[]>(`/customers/${id}/minute_grants/`);
    return res.data;
  },
  plans: async () => {
    const res = await api.get<CustomerPlan[] | Paginated<CustomerPlan>>(
      "/customers/plans/",
    );
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  createPlan: async (data: CustomerPlanInput) => {
    const res = await api.post<CustomerPlan>("/customers/plans/", data);
    return res.data;
  },
  updatePlan: async (id: string, data: Partial<CustomerPlanInput>) => {
    const res = await api.patch<CustomerPlan>(`/customers/plans/${id}/`, data);
    return res.data;
  },
  removePlan: async (id: string) => {
    await api.delete(`/customers/plans/${id}/`);
  },
  billingPreview: async () => {
    const res = await api.get<BillingPreview>("/customers/billing_preview/");
    return res.data;
  },
  assignable: async (customerId?: string) => {
    const res = await api.get<Assignable>("/customers/assignable/", {
      params: customerId ? { customer: customerId } : {},
    });
    return res.data;
  },
};

// ── Customer portal (what a customer login sees) ────────────────────────────
export type PortalSummary = {
  customer: { id: string; name: string; email: string; status: string };
  assistants: CustomerRef[];
  phone_numbers: { id: string; number: string; label: string }[];
  usage: { minutes_this_month: number; calls_total: number };
};

export type PortalCall = {
  id: string;
  from_number: string;
  to_number: string;
  direction: "inbound" | "outbound";
  status: string;
  started_at: string | null;
  duration_seconds: number;
};

/** The customer's own plan page: what they bought, and what is left of it. */
export type MyPlan = CustomerUsage & {
  customer: { id: string; name: string; email: string; status: string };
  next_charge_at: string | null;
  minute_grants: MinuteGrant[];
};

export const portalApi = {
  summary: async () => {
    const res = await api.get<PortalSummary>("/customers/portal/summary/");
    return res.data;
  },
  myPlan: async () => {
    const res = await api.get<MyPlan>("/customers/portal/plan/");
    return res.data;
  },
  calls: async (offset = 0, limit = 50) => {
    const res = await api.get<{ count: number; results: PortalCall[] }>(
      "/customers/portal/calls/",
      { params: { offset, limit } },
    );
    return res.data;
  },
};
